import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Copy, ShieldCheck } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Input } from '../../../shared/components/ui/Input.js';
import { useAuthStore } from '../../../stores/auth-store.js';
import { confirmSetupTotp, getSetupTotp } from '../api.js';

export function SetupTotpPage() {
  const [code, setCode] = useState('');
  const navigate = useNavigate();
  const pendingToken = useAuthStore((s) => s.pendingToken);
  const setUser = useAuthStore((s) => s.setUser);
  const setPendingToken = useAuthStore((s) => s.setPendingToken);

  useEffect(() => {
    if (!pendingToken) {
      void navigate({ to: '/login' });
    }
  }, [pendingToken, navigate]);

  const setupQuery = useQuery({
    queryKey: ['setup-totp', pendingToken],
    queryFn: async () => getSetupTotp(pendingToken ?? ''),
    enabled: !!pendingToken,
    refetchOnMount: 'always',
    staleTime: Infinity,
  });

  const confirmMut = useMutation({
    mutationFn: async () => confirmSetupTotp(pendingToken ?? '', code),
    onSuccess: (data) => {
      setUser(data.user);
      setPendingToken(null);
      toast.success('TOTP configurado com sucesso!');
      void navigate({ to: '/' });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  const copySecret = async (): Promise<void> => {
    if (!setupQuery.data) return;
    await navigator.clipboard.writeText(setupQuery.data.secret);
    toast.success('Secret copiado');
  };

  if (!pendingToken) return null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-n-50 p-6">
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          confirmMut.mutate();
        }}
        className="w-full max-w-[460px] rounded-lg border border-n-200 bg-white p-8 shadow-md"
      >
        <div className="mb-6 flex flex-col items-center gap-3">
          <div className="flex h-[56px] w-[56px] items-center justify-center rounded-lg bg-brand-500 text-white">
            <ShieldCheck size={28} />
          </div>
          <div className="text-center">
            <h1 className="text-[20px] font-bold text-n-900">Configurar TOTP</h1>
            <p className="text-[13px] text-n-500">
              Use Google Authenticator, Authy ou 1Password
            </p>
          </div>
        </div>

        {setupQuery.isLoading && (
          <div className="py-12 text-center text-[13px] text-n-500">Gerando QR code...</div>
        )}

        {setupQuery.data && (
          <>
            <div className="mb-4 flex flex-col items-center gap-3">
              <img
                src={setupQuery.data.qr_data_url}
                alt="QR Code TOTP"
                width={200}
                height={200}
                className="rounded-md border border-n-200"
              />
              <div className="flex w-full items-center gap-2 rounded-md bg-n-50 px-3 py-2">
                <code className="flex-1 break-all font-mono text-[11px] text-n-700">
                  {setupQuery.data.secret}
                </code>
                <button
                  type="button"
                  onClick={copySecret}
                  className="rounded-md p-1.5 text-n-500 hover:bg-n-100 hover:text-n-700"
                  title="Copiar secret"
                >
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-[11px] text-n-500 text-center">
                Escaneie o QR ou cole o secret no app. Depois digite o código abaixo.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <Input
                label="Código gerado pelo app"
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="\d{6}"
                autoComplete="one-time-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
                className="text-center tracking-[0.5em] font-mono text-[18px]"
              />
              <Button type="submit" size="lg" className="w-full" loading={confirmMut.isPending}>
                Confirmar
              </Button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
